import { SpaceBetween } from '@risk-smart/themed-cloudscape-components';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useDeleteQuestionnaireTemplate } from 'src/hooks/mutations/questionnaire-template';
import { useGetQuestionnaireTemplateById } from 'src/hooks/queries/questionnaire-template/useGetQuestionnaireTemplateById';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';

import {
  useGetDetailParentPath,
  useGetDetailPath,
} from '../../../routes/useGetDetailParentPath';

interface Props {
  activeTabId?: 'details' | 'versions';
}

const Page: FC<Props> = ({ activeTabId }: Props) => {
  const questionnaireTemplateId = useGetGuidParam('questionnaireTemplateId');
  const entityUrl = useGetDetailPath(questionnaireTemplateId);
  const parentUrl = useGetDetailParentPath(questionnaireTemplateId);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const { data } = useGetQuestionnaireTemplateById({
    queryArgs: { id: questionnaireTemplateId },
  });

  const navigate = useNavigate();

  // `_by_pk` returns a nullable single row; coalesce null → undefined so the
  // entity matches the `ObjectWithContributors | undefined` shape the toolbar
  // `parentObject` and `TabSettingsModal` `parent` props expect (mirrors the
  // `data?.x?.[0]` `| undefined` shape the other detail pages derive).
  const questionnaireTemplate = data?.questionnaire_template ?? undefined;
  const title = questionnaireTemplate?.Title || '';
  const tabs = useTabs({
    parentType: Parent_Type_Enum.QuestionnaireTemplate,
    hrefRoot: entityUrl,
    parent: null,
  });
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'questionnaire_templates',
  });

  const { deleteQuestionnaireTemplate, loading } =
    useDeleteQuestionnaireTemplate();

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!data) {
        throw new Error('Data is not available');
      }

      await deleteQuestionnaireTemplate({
        variables: {
          Id: questionnaireTemplateId,
        },
      });

      await navigate(parentUrl);

      return true;
    },
  });

  // Object-level toolbar (CUS-171) — flag-gated. Relocates Delete into the
  // toolbar's danger slot and surfaces the Save disc / Cancel X (save state
  // bridged from the active tab's form via useObjectToolbarFormStore). The
  // "Edit" overflow group hosts the relocated form/tab-settings openers.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      parentObject: questionnaireTemplate,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:questionnaire_template',
            // Hydration gate (ENG-616) — `onDelete` short-circuits on `!data`.
            disabled: !questionnaireTemplate,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [title, questionnaireTemplate, formConfig, t, st]
  );

  return (
    <PageLayout
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Permission
            permission={'delete:questionnaire_template'}
            parentObject={data?.questionnaire_template}
          >
            <Button
              variant={'normal'}
              formAction={'none'}
              // Wait for the entity to hydrate. `onDelete` throws
              // `Data is not available` when `data` is absent, which
              // `mutationResultNotification` surfaces as a bare "Failed to save
              // changes" with NO request issued, so a click inside the hydration
              // window looks like a broken delete (ENG-616). Gated on the
              // template itself rather than `data` so an empty result keeps the
              // button inert too. Disabling also restores Playwright's
              // actionability wait.
              disabled={!data?.questionnaire_template}
              onClick={() => setIsDeleteModalVisible(true)}
            >
              {st('delete_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.QuestionnaireTemplate}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={questionnaireTemplate}
      />
      <DeleteModal
        loading={loading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
        size={'medium'}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
